import type { DelegationView } from '@nest-recruit/shared';
import { addDhakaDays, dhakaDayStartIso } from '../../../../lib/dhaka-time';

/**
 * Delegation's timeline (Admin redesign 2026-11-02, Option A of `2-people/delegation.html`): five weeks, one before this one and
 * three after it, so "is anyone away next week, and is someone covering?" is answered in one look. Weeks start on Sunday, as the
 * interview calendar's do. All of it is Dhaka days (D5, D36).
 */
export const TIMELINE_WEEKS = 5;
const DAYS_PER_WEEK = 7;

export interface TimelineWindow {
  /** The Sunday each week starts on, `YYYY-MM-DD`. */
  readonly weeks: readonly string[];
  readonly startMs: number;
  readonly endMs: number;
}

/** The five weeks shown; `shiftWeeks` moves them (the ‹ and › buttons). */
export function timelineWindow(today: string, shiftWeeks = 0): TimelineWindow {
  const weekday = new Date(`${today}T00:00:00Z`).getUTCDay();
  const first = addDhakaDays(today, -weekday + DAYS_PER_WEEK * (shiftWeeks - 1));
  const weeks = Array.from({ length: TIMELINE_WEEKS }, (_, index) => addDhakaDays(first, DAYS_PER_WEEK * index));
  return {
    weeks,
    startMs: Date.parse(dhakaDayStartIso(first) ?? first),
    endMs: Date.parse(dhakaDayStartIso(addDhakaDays(first, DAYS_PER_WEEK * TIMELINE_WEEKS)) ?? first),
  };
}

/** How far across the window an instant is, 0 to 1; null outside it (the red today line is then not drawn). */
export function fractionOf(window: TimelineWindow, instantMs: number): number | null {
  if (instantMs < window.startMs || instantMs >= window.endMs) return null;
  return (instantMs - window.startMs) / (window.endMs - window.startMs);
}

/**
 * Where the stand-in's name goes. A bar wide enough holds it; a short one has it beside the bar, on the side with more room, so the
 * name is never cut off (CRITIQUE: "Mehedi", "Tahmina" and "Ar" were clipped inside narrow bars).
 */
export type LabelSide = 'inside' | 'after' | 'before';

/** A bar at least this share of the five weeks (about 12 days) holds "Farhana Yasmin covers" at a laptop's width. */
const INSIDE_MIN_PERCENT = 35;

export interface PlacedBar {
  /** Percent of the five weeks from the left edge. */
  readonly left: number;
  readonly width: number;
  readonly label: LabelSide;
  /** Percent of the five weeks the name may use, on its side. */
  readonly labelRoom: number;
}

const round = (value: number): number => Math.round(value * 10) / 10;

/** A cover's bar, cut to the window; null when none of it falls inside, or it was cancelled before it began. */
export function placeBar(cover: Pick<DelegationView, 'startsAt' | 'endsAt' | 'revokedAt'>, window: TimelineWindow): PlacedBar | null {
  const starts = Date.parse(cover.startsAt);
  const ends = cover.revokedAt === null ? Date.parse(cover.endsAt) : Math.min(Date.parse(cover.endsAt), Date.parse(cover.revokedAt));
  if (ends <= starts || ends <= window.startMs || starts >= window.endMs) return null;

  const span = window.endMs - window.startMs;
  const left = round((Math.max(starts, window.startMs) - window.startMs) / span * 100);
  const right = round((Math.min(ends, window.endMs) - window.startMs) / span * 100);
  const width = round(right - left);

  if (width >= INSIDE_MIN_PERCENT) return { left, width, label: 'inside', labelRoom: width };
  const after = round(100 - right);
  return after >= left ? { left, width, label: 'after', labelRoom: after } : { left, width, label: 'before', labelRoom: left };
}

export interface TimelineRow {
  readonly fromUserId: string;
  readonly fromUserName: string;
  readonly bars: readonly { readonly cover: DelegationView; readonly bar: PlacedBar }[];
}

/** One row per person who is away in the window, in the order their first cover starts. */
export function timelineRows(covers: readonly DelegationView[], window: TimelineWindow): readonly TimelineRow[] {
  const rows = new Map<string, { fromUserId: string; fromUserName: string; bars: { cover: DelegationView; bar: PlacedBar }[] }>();
  for (const cover of covers) {
    const bar = placeBar(cover, window);
    if (bar === null) continue;
    const row = rows.get(cover.fromUserId) ?? { fromUserId: cover.fromUserId, fromUserName: cover.fromUserName, bars: [] };
    row.bars.push({ cover, bar });
    rows.set(cover.fromUserId, row);
  }
  for (const row of rows.values()) row.bars.sort((a, b) => a.bar.left - b.bar.left);
  return [...rows.values()].sort((a, b) => (a.bars[0]?.bar.left ?? 0) - (b.bars[0]?.bar.left ?? 0));
}
